import React, { useState, useEffect } from 'react';
import LetterCanvas from '../molecules/LetterCanvas';
import ActionButton from '../atoms/ActionButton';
import ShareLinks from '../molecules/ShareLinks';
import Ads from '../atoms/Ads';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { LetterData, useLetterStore } from '../../store/letterStore';
import LetterLoading from '../molecules/LetterLoading';
import LetterLoadingSkelton from '../molecules/LetterLoadingSkelton';
import LetterNotFound from '../molecules/LetterNotFound';
import { supabase } from '../../lib/supabaseClient';
import { useAiStore } from '@/store/aiStore';

const LetterView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { clearLetterData } = useLetterStore();
  const { clearAiData } = useAiStore();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [letterData, setLetterData] = useState<LetterData | null>(null);

  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [editable, setEditable] = useState<boolean>(false);

  const handleOutput = (url: string) => {
    setDownloadUrl(url);
  };

  const saveCanvas = () => {
    const canvas = document.getElementById('js-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'letter.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const makeLetterAgain = () => {
    clearLetterData();
    clearAiData();
    navigate('/question');
  };

  useEffect(() => {
    const fetchLetterById = async (id: string) => {
      try {
        const { data, error } = await supabase
          .from('letters')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          throw error;
        }

        // end_dateが過ぎているかチェック（23:59まで有効）
        if (data.end_date) {
          const endDateTime = new Date(data.end_date);
          endDateTime.setHours(23, 59, 59, 999); // その日の23:59:59.999に設定

          if (endDateTime < new Date()) {
            throw new Error('このメッセージの有効期限が過ぎています');
          }
        }

        setLetterData(data);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setError(
          error instanceof Error ? error.message : 'データの取得に失敗しました'
        );
        setIsLoading(false);
      }
    };

    if (id) {
      fetchLetterById(id);
    }
  }, [id]);

  if (isLoading) {
    return (
      <div id='page' className='letter-page'>
        {/* ローディング中は常に表示 */}
        <LetterLoadingSkelton />
      </div>
    );
  }

  if (error || !letterData) {
    return (
      <div id='page' className='letter-page'>
        <header className='l-header p-header'>
          <h1 className='p-header__logo'>
            <Link to='/'>
              <img src='/img/common/logo.png' alt='SKALE' />
            </Link>
          </h1>
        </header>

        <div id='contents'>
          <LetterNotFound
            title={
              error ? 'エラーが発生しました' : 'メッセージが見つかりません'
            }
            message={
              error ||
              '指定されたURLのメッセージは存在しないか、削除された可能性があります。'
            }
          />
        </div>

        <footer className='l-footer p-footer'>
          <Ads />
        </footer>
      </div>
    );
  }

  return (
    <div id='page' className='letter-page'>
      <header className='l-header p-header'>
        <h1 className='p-header__logo'>
          <Link to='/'>
            <img src='/img/common/logo.png' alt='SKALE' />
          </Link>
        </h1>
      </header>

      <div id='contents'>
        <div className='c-container'>
          <div className='p-letter u-mb40'>
            <div className='p-letter__logo'>
              <img src='/img/common/ic-logo.png' alt='SKALE' />
            </div>
          </div>

          <LetterCanvas
            template_file_name={
              letterData.template_name || 'letter-template01.jpg'
            }
            from_name={letterData.from_name || ''}
            to_name={letterData.to_name || ''}
            message={letterData.message || ''}
            date={letterData.date || ''}
            onOutput={handleOutput}
          />
        </div>

        <ShareLinks fromName={letterData.from_name || ''} />

        <div className='c-btn-width u-mt90'>
          <ActionButton onClick={makeLetterAgain}>
            新しくメッセージを作成
          </ActionButton>
        </div>
      </div>

      <footer className='l-footer p-footer'>
        <Ads />
      </footer>

      <LetterLoading />
    </div>
  );
};

export default LetterView;
